import { Controller } from 'stimulus'
import h from 'hyperscript'

import { getCurrencyController } from '../../_js/controllers/currency-controller'
import { getPlansController } from '../../_js/controllers/plans-controller'

import { getCurrencySet, getPlanPriceValueByKey } from '../../_js/base/plans'

export default class CurrencySelector extends Controller {
  static values = {
    /**
     * Visual type of the selector
     * - "select" -> it will render a select with the available currencies as options
     * - "button" -> it will render the available currencies as buttons
     */
    type: String,

    /**
     * Query selector that will force the parent to be hidden/visible when the
     * currency selector is visible or not.
     *
     * Useful if the selector is around the content elements that don't make sense to show
     * without the selector (copy, input labels, ...)
     */
    parent: String,

    /**
     * Key of a "plan Price Value" that will show a specific amount when selecting a currency
     */
    planPrice: String,
  }

  currencyState = null

  currencyObservableUnsubscribe = null

  planPriceState = null

  connect() {
    const currencyController = getCurrencyController(this.application)
    const currencyObservable = currencyController.getObservable()

    const state = currencyObservable.state()
    this.currencyState = state

    this.currencyObservableUnsubscribe = currencyObservable.subscribe((newState) => {
      this.currencyState = newState
      this.renderCurrencySelector()
    })

    if (this.planPriceValue) {
      const plans = getPlansController(this.application)
      const planObservable = plans.getObservable()

      planObservable.subscribe((planState) => {
        this.planPriceState = planState

        if (!this.currencyState) {
          return
        }

        this.renderCurrencySelector()
      })
    }

    if (!state) {
      return
    }

    this.renderCurrencySelector()
  }

  renderCurrencySelector() {
    const { availableCurrencies } = this.currencyState
    this.element.textContent = ''

    const showSelector = availableCurrencies.length > 1

    if (showSelector) {
      const element = this.createCurrencySelector(this.currencyState)

      if (Array.isArray(element)) {
        element.forEach((el) => {
          this.element.append(el)
        })
      } else {
        this.element.append(element)
      }
    }

    if (this.parentValue) {
      const parentElement = document.querySelector(this.parentValue)
      parentElement?.setAttribute('hidden', !showSelector)
    }
  }

  setCurrency(currency) {
    const currencyController = getCurrencyController(this.application)
    currencyController.setCurrency(currency)
  }

  createCurrencySelector({ currency, availableCurrencies }) {
    if (this.typeValue === 'select') {
      const { textShort, textLong, icon } = getCurrencySet(currency)

      let additionalText = ''

      if (this.planPriceState && this.planPriceValue) {
        additionalText = getPlanPriceValueByKey(this.planPriceState.plans, this.planPriceValue, currency)
      }

      return h('span.currency-selector-selector', {}, [
        h(
          'select.currency-selector-selector__select.button.button--tertiary.button--medium',
          { onchange: (e) => this.setCurrency(e.target.value), name: 'currency' },
          availableCurrencies.map((itemCurrency) =>
            h('option', { selected: currency === itemCurrency, value: itemCurrency }, getCurrencySet(itemCurrency).text)
          )
        ),
        h('span.currency-selector-selector__fake-button', {}, [
          h('img.currency-selector-selector__icon', { src: icon }),
          h('abbr', { title: textLong }, textShort),
          h('span', {}, additionalText),
          h('img.currency-selector-selector__chevron', { src: '/images/icons-prism/24x24/chevrons.up.down.24.svg' }),
        ]),
      ])
    }

    if (this.typeValue === 'button') {
      return availableCurrencies.map((itemCurrency) => {
        const { textLong, icon } = getCurrencySet(itemCurrency)

        return h(
          [
            'button',
            'button',
            'button--small',
            'currency-selector-button',
            currency === itemCurrency ? 'button--primary' : 'button--secondary',
          ].join('.'),
          { onclick: () => this.setCurrency(itemCurrency) },
          [h('img.currency-selector-button__icon', { src: icon }), textLong]
        )
      })
    }

    return null
  }

  disconnect() {
    this.this.currencyObservableUnsubscribe?.()
  }
}
